import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import type { Locale } from '@mrigl/shared';
import { Alert, Button, SelectField, TextAreaField, TextField } from '@mrigl/ui';
import { useCallback, useEffect, useId, useState, type SubmitEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { Loading } from '../../components.js';
import { useI18n, type I18n } from '../../i18n.js';
import {
  display,
  moveItem,
  storeLocales,
  useStore,
  type Localized,
  type StoreDetails,
} from '../catalog/shared.js';

type ThemeState = Schemas['ThemeState'];
type Theme = ThemeState['draft'];
type Section = Theme['home']['sections'][number];
type SectionType = Section['type'];
type ThemeLink = Theme['settings']['navigation'][number]['link'];
type Colors = Theme['settings']['colors'];
type Publication = Schemas['ThemePublicationList']['items'][number];

const SECTION_TYPES: readonly SectionType[] = [
  'hero',
  'featured-collection',
  'product-grid',
  'rich-text',
  'image-with-text',
];
const MIN_CONTRAST = 4.5;

interface Choices {
  readonly media: readonly { id: string; name: string }[];
  readonly collections: readonly { id: string; name: string }[];
  readonly products: readonly { id: string; name: string }[];
}

/** WCAG 2.1 contrast ratio, as the API checks it. */
export function contrast(a: string, b: string): number {
  const luminance = (hex: string) => {
    const [r = 0, g = 0, bl = 0] = [1, 3, 5]
      .map((start) => Number.parseInt(hex.slice(start, start + 2), 16) / 255)
      .map((value) => (value <= 0.039_28 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4));
    return 0.2126 * r + 0.7152 * g + 0.0722 * bl;
  };
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (light + 0.05) / (dark + 0.05);
}

/** A new section of `type` with neutral defaults. */
export function newSection(type: SectionType, collectionId: string | undefined): Section | null {
  const id = `${type}-${Math.random().toString(36).slice(2, 8)}`;
  switch (type) {
    case 'hero':
      return {
        id,
        type,
        heading: {},
        text: {},
        imageMediaId: null,
        buttonLabel: {},
        buttonLink: null,
      };
    case 'featured-collection':
      return collectionId === undefined ? null : { id, type, heading: {}, collectionId, limit: 8 };
    case 'product-grid':
      return { id, type, heading: {}, limit: 12 };
    case 'rich-text':
      return { id, type, heading: {}, body: {} };
    case 'image-with-text':
      return { id, type, heading: {}, body: {}, imageMediaId: null, imagePosition: 'start' };
  }
}

const RESERVED_HANDLES = new Set([
  'products',
  'collections',
  'cart',
  'checkout',
  'search',
  'orders',
  'reviews',
  'feedback',
  'pages',
]);
const TEMPLATES = ['souk', 'atelier', 'ledger'] as const;
const SLOT_NAMES = ['product', 'collection', 'cart'] as const;

function linkValue(link: ThemeLink | null): string {
  if (link === null) return '';
  switch (link.type) {
    case 'home':
    case 'search':
      return link.type;
    case 'collection':
      return `collection:${link.collectionId}`;
    case 'product':
      return `product:${link.productId}`;
    case 'page':
      return `page:${link.pageId}`;
  }
}

function parseLink(value: string): ThemeLink | null {
  if (value === 'home' || value === 'search') return { type: value };
  const separator = value.indexOf(':');
  if (separator < 0) return null;
  const kind = value.slice(0, separator);
  const id = value.slice(separator + 1);
  if (kind === 'collection') return { type: 'collection', collectionId: id };
  if (kind === 'product') return { type: 'product', productId: id };
  if (kind === 'page') return { type: 'page', pageId: id };
  return null;
}

function nextHandle(pages: readonly { handle: string }[]): string {
  const taken = new Set(pages.map((page) => page.handle));
  let number = pages.length + 1;
  let handle = `page-${String(number)}`;
  while (taken.has(handle)) {
    number += 1;
    handle = `page-${String(number)}`;
  }
  return handle;
}

function pageIssue(pages: readonly { handle: string }[]): 'reserved' | 'duplicate' | null {
  if (
    pages.some(
      (page) => !/^[a-z0-9-]{1,60}$/.test(page.handle) || RESERVED_HANDLES.has(page.handle),
    )
  ) {
    return 'reserved';
  }
  if (new Set(pages.map((page) => page.handle)).size !== pages.length) return 'duplicate';
  return null;
}

function themeError(t: I18n['t'], failure: unknown): string {
  if (failure instanceof ApiError && failure.code === 'version-conflict')
    return t('admin.theme.error.version-conflict');
  if (failure instanceof ApiError && failure.code === 'validation-failed')
    return t('admin.product.error.validation');
  return errorMessage(t, failure);
}

/** Storefront design editor: colours, logo, menu, home sections, preview and publication. */
export function ThemePage() {
  const { storeId = '' } = useParams();
  const { t } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [state, setState] = useState<ThemeState | null>(null);
  const [choices, setChoices] = useState<Choices | null>(null);
  const [history, setHistory] = useState<Publication[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Kept here: the editor remounts with every new version.
  const [notice, setNotice] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    const list = await unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/theme/publications', {
        params: { path: { storeId } },
      }),
    );
    setHistory(list.items);
  }, [storeId]);

  useEffect(() => {
    void (async () => {
      try {
        const [theme, media, collections, products] = await Promise.all([
          unwrap(
            api.GET('/admin-api/v1/stores/{storeId}/theme', { params: { path: { storeId } } }),
          ),
          unwrap(
            api.GET('/admin-api/v1/stores/{storeId}/media', {
              params: { path: { storeId }, query: { limit: 100 } },
            }),
          ).catch(() => ({ items: [] as Schemas['MediaAsset'][] })),
          unwrap(
            api.GET('/admin-api/v1/stores/{storeId}/collections', {
              params: { path: { storeId }, query: { limit: 100 } },
            }),
          ),
          unwrap(
            api.GET('/admin-api/v1/stores/{storeId}/products', {
              params: { path: { storeId }, query: { pageSize: 100, sort: 'title' } },
            }),
          ),
        ]);
        setState(theme);
        setChoices({
          media: media.items.map((item) => ({ id: item.mediaId, name: item.filename })),
          collections: collections.items.map((item) => ({
            id: item.collectionId,
            name: Object.values(item.title)[0] ?? item.handle,
          })),
          products: products.items.map((item) => ({
            id: item.productId,
            name: Object.values(item.title)[0] ?? item.handle,
          })),
        });
        await loadHistory();
      } catch (failure) {
        setError(errorMessage(t, failure));
      }
    })();
  }, [storeId, loadHistory, t]);

  const failure = storeError ?? error;
  if (failure !== null) return <Alert tone="error">{failure}</Alert>;
  if (store === null || state === null || choices === null) return <Loading />;
  return (
    <Editor
      key={state.version}
      store={store}
      state={state}
      choices={choices}
      history={history}
      notice={notice}
      previewUrl={previewUrl}
      onNotice={setNotice}
      onPreview={setPreviewUrl}
      onState={(next) => {
        setState(next);
        void loadHistory().catch(() => undefined);
      }}
    />
  );
}

function Editor({
  store,
  state,
  choices,
  history,
  notice,
  previewUrl,
  onNotice,
  onPreview,
  onState,
}: {
  readonly store: StoreDetails;
  readonly state: ThemeState;
  readonly choices: Choices;
  readonly history: readonly Publication[];
  readonly notice: string | null;
  readonly previewUrl: string | null;
  readonly onNotice: (notice: string) => void;
  readonly onPreview: (url: string) => void;
  readonly onState: (state: ThemeState) => void;
}) {
  const { t, locale } = useI18n();
  const locales = storeLocales(store);
  const canManage = store.permissions.includes('theme.manage');
  const [theme, setTheme] = useState<Theme>(state.draft);
  const pages = theme.pages;
  const slots = theme.slots;
  const [dirty, setDirty] = useState(false);
  const status =
    notice ?? t(state.hasUnpublishedChanges ? 'admin.theme.unpublished' : 'admin.theme.upToDate');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const languageName = (code: Locale) => t(`admin.language.${code}`);

  const change = (next: Theme) => {
    setTheme(next);
    setDirty(true);
  };
  const setSettings = (settings: Partial<Theme['settings']>) => {
    change({ ...theme, settings: { ...theme.settings, ...settings } });
  };
  const setSections = (sections: Section[]) => {
    change({ ...theme, home: { sections } });
  };
  const setPages = (next: Theme['pages']) => {
    change({ ...theme, pages: next });
  };
  const setSlotSide = (
    slot: (typeof SLOT_NAMES)[number],
    side: 'before' | 'after',
    sections: Section[],
  ) => {
    change({
      ...theme,
      slots: { ...slots, [slot]: { ...slots[slot], [side]: sections } },
    });
  };
  const handles = pageIssue(pages);

  const linkOptions = [
    { value: '', label: t('admin.theme.link.none') },
    { value: 'home', label: t('admin.theme.link.home') },
    { value: 'search', label: t('admin.theme.link.search') },
    ...pages.map((page) => ({
      value: `page:${page.id}`,
      label: t('admin.theme.link.page', {
        name: display(page.title, locale, store, page.handle),
      }),
    })),
    ...choices.collections.map((item) => ({
      value: `collection:${item.id}`,
      label: t('admin.theme.link.collection', { name: item.name }),
    })),
    ...choices.products.map((item) => ({
      value: `product:${item.id}`,
      label: t('admin.theme.link.product', { name: item.name }),
    })),
  ];
  const imageOptions = [
    { value: '', label: t('admin.theme.noImage') },
    ...choices.media.map((item) => ({ value: item.id, label: item.name })),
  ];

  async function run(action: () => Promise<ThemeState>, message: string) {
    setBusy(true);
    setError(null);
    try {
      const next = await action();
      setDirty(false);
      onState(next);
      onNotice(message);
    } catch (failure) {
      setError(themeError(t, failure));
    } finally {
      setBusy(false);
    }
  }

  const saveDraft = () =>
    unwrap(
      api.PUT('/admin-api/v1/stores/{storeId}/theme', {
        params: { path: { storeId: store.storeId } },
        body: { document: theme, version: state.version },
      }),
    );

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    await run(saveDraft, t('admin.theme.saved'));
  }

  async function apply(templateId: (typeof TEMPLATES)[number]) {
    if (!window.confirm(t('admin.theme.template.confirm'))) return;
    await run(
      () =>
        unwrap(
          api.POST('/admin-api/v1/stores/{storeId}/theme/template', {
            params: { path: { storeId: store.storeId } },
            body: { templateId, version: state.version },
          }),
        ),
      t('admin.theme.template.applied'),
    );
  }

  async function publish() {
    await run(async () => {
      const saved = dirty ? await saveDraft() : state;
      return unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/theme/publish', {
          params: { path: { storeId: store.storeId } },
          body: { version: saved.version },
        }),
      );
    }, t('admin.theme.published'));
  }

  async function preview() {
    setBusy(true);
    setError(null);
    try {
      if (dirty) {
        const saved = await saveDraft();
        setDirty(false);
        onState(saved);
      }
      const link = await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/theme/preview', {
          params: { path: { storeId: store.storeId } },
        }),
      );
      onPreview(link.url);
    } catch (failure) {
      setError(themeError(t, failure));
    } finally {
      setBusy(false);
    }
  }

  const colors = theme.settings.colors;
  const textContrast = contrast(colors.text, colors.background);
  const buttonContrast = contrast(colors.accentText, colors.accent);
  const colorField = (key: keyof Colors) => (
    <ColorField
      key={key}
      label={t(`admin.theme.color.${key}`)}
      value={colors[key]}
      onChange={(value) => {
        setSettings({ colors: { ...colors, [key]: value } });
      }}
    />
  );

  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${store.storeId}`}>
          {display(store.name, locale, store, store.slug)}
        </Link>
      </p>
      <h1 className="mrigl-heading">{t('admin.theme.title')}</h1>
      <div role="status">
        <Alert tone="success">{status}</Alert>
      </div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <form
        className="mrigl-stack"
        aria-label={t('admin.theme.title')}
        onSubmit={(event) => void save(event)}
      >
        <fieldset className="mrigl-card mrigl-stack" disabled={!canManage}>
          <legend className="mrigl-subheading">{t('admin.theme.template')}</legend>
          <p className="mrigl-muted">{t('admin.theme.template.hint')}</p>
          <div className="admin-actions">
            {TEMPLATES.map((id) => (
              <Button
                key={id}
                type="button"
                variant="secondary"
                busy={busy}
                onClick={() => void apply(id)}
              >
                {t('admin.theme.template.apply', { name: t(`admin.theme.template.${id}`) })}
              </Button>
            ))}
          </div>
        </fieldset>

        <fieldset className="mrigl-card mrigl-stack" disabled={!canManage}>
          <legend className="mrigl-subheading">{t('admin.theme.colors')}</legend>
          <div className="admin-filters">
            {colorField('background')}
            {colorField('text')}
          </div>
          {textContrast < MIN_CONTRAST ? (
            <Alert tone="error">
              {t('admin.theme.contrastLow', { ratio: textContrast.toFixed(1) })}
            </Alert>
          ) : null}
          <div className="admin-filters">
            {colorField('accent')}
            {colorField('accentText')}
          </div>
          {buttonContrast < MIN_CONTRAST ? (
            <Alert tone="error">
              {t('admin.theme.contrastLow', { ratio: buttonContrast.toFixed(1) })}
            </Alert>
          ) : null}
          <SelectField
            label={t('admin.theme.logo')}
            value={theme.settings.logoMediaId ?? ''}
            onChange={(event) => {
              setSettings({ logoMediaId: event.target.value === '' ? null : event.target.value });
            }}
            options={imageOptions}
          />
          {locales.map((code) => (
            <TextField
              key={code}
              lang={code}
              dir="auto"
              maxLength={200}
              label={t('admin.theme.announcementIn', { language: languageName(code) })}
              value={theme.settings.announcement[code] ?? ''}
              onChange={(event) => {
                setSettings({
                  announcement: { ...theme.settings.announcement, [code]: event.target.value },
                });
              }}
            />
          ))}
        </fieldset>

        <fieldset className="mrigl-card mrigl-stack" disabled={!canManage}>
          <legend className="mrigl-subheading">{t('admin.theme.navigation')}</legend>
          {theme.settings.navigation.map((item, index) => {
            const number = index + 1;
            const setItem = (next: typeof item) => {
              setSettings({
                navigation: theme.settings.navigation.map((other, position) =>
                  position === index ? next : other,
                ),
              });
            };
            return (
              <div key={`nav-${String(index)}`} className="admin-option mrigl-stack">
                {locales.map((code) => (
                  <TextField
                    key={code}
                    lang={code}
                    dir="auto"
                    maxLength={40}
                    label={t('admin.theme.navLabelIn', { number, language: languageName(code) })}
                    value={item.label[code] ?? ''}
                    onChange={(event) => {
                      setItem({ ...item, label: { ...item.label, [code]: event.target.value } });
                    }}
                  />
                ))}
                <SelectField
                  label={t('admin.theme.linkFor', { number })}
                  value={linkValue(item.link)}
                  onChange={(event) => {
                    const link = parseLink(event.target.value);
                    if (link !== null) setItem({ ...item, link });
                  }}
                  options={linkOptions.slice(1)}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setSettings({
                      navigation: theme.settings.navigation.filter(
                        (_, position) => position !== index,
                      ),
                    });
                  }}
                >
                  {t('admin.theme.removeNav', { number })}
                </Button>
              </div>
            );
          })}
          {theme.settings.navigation.length < 8 ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setSettings({
                  navigation: [...theme.settings.navigation, { label: {}, link: { type: 'home' } }],
                });
              }}
            >
              {t('admin.theme.addNavItem')}
            </Button>
          ) : null}
        </fieldset>

        <fieldset className="mrigl-card mrigl-stack" disabled={!canManage}>
          <legend className="mrigl-subheading">{t('admin.theme.sections')}</legend>
          <SectionList
            sections={theme.home.sections}
            max={20}
            locales={locales}
            choices={choices}
            imageOptions={imageOptions}
            linkOptions={linkOptions}
            typeLabel={t('admin.theme.sectionType')}
            addLabel={t('admin.theme.addSection')}
            onChange={setSections}
          />
        </fieldset>

        <fieldset className="mrigl-card mrigl-stack" disabled={!canManage}>
          <legend className="mrigl-subheading">{t('admin.theme.pages')}</legend>
          {handles === null ? null : (
            <Alert tone="error">{t(`admin.theme.${handles}Handle`)}</Alert>
          )}
          {pages.map((page, index) => {
            const number = index + 1;
            const setPage = (next: Theme['pages'][number]) => {
              setPages(pages.map((other, position) => (position === index ? next : other)));
            };
            return (
              <div key={page.id} className="admin-option mrigl-stack">
                <TextField
                  label={t('admin.theme.pageHandle', { number })}
                  value={page.handle}
                  maxLength={60}
                  dir="ltr"
                  onChange={(event) => {
                    setPage({
                      ...page,
                      handle: event.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9-]/g, '')
                        .slice(0, 60),
                    });
                  }}
                />
                {locales.map((code) => (
                  <TextField
                    key={code}
                    lang={code}
                    dir="auto"
                    maxLength={120}
                    label={t('admin.theme.pageTitleIn', { number, language: languageName(code) })}
                    value={page.title[code] ?? ''}
                    onChange={(event) => {
                      setPage({ ...page, title: { ...page.title, [code]: event.target.value } });
                    }}
                  />
                ))}
                <SectionList
                  sections={page.sections}
                  max={20}
                  locales={locales}
                  choices={choices}
                  imageOptions={imageOptions}
                  linkOptions={linkOptions}
                  typeLabel={t('admin.theme.pageSectionType', { number })}
                  addLabel={t('admin.theme.addPageSection', { number })}
                  onChange={(sections) => {
                    setPage({ ...page, sections });
                  }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setPages(pages.filter((other) => other.id !== page.id));
                  }}
                >
                  {t('admin.theme.removePage', { number })}
                </Button>
              </div>
            );
          })}
          {pages.length < 20 ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setPages([
                  ...pages,
                  {
                    id: `page-${Math.random().toString(36).slice(2, 10)}`,
                    handle: nextHandle(pages),
                    title: {},
                    sections: [],
                  },
                ]);
              }}
            >
              {t('admin.theme.addPage')}
            </Button>
          ) : null}
        </fieldset>

        {SLOT_NAMES.map((slot) => (
          <fieldset key={slot} className="mrigl-card mrigl-stack" disabled={!canManage}>
            <legend className="mrigl-subheading">{t(`admin.theme.slots.${slot}`)}</legend>
            {(['before', 'after'] as const).map((side) => (
              <div key={side} className="mrigl-stack">
                <h3 className="mrigl-subheading">{t(`admin.theme.slots.${side}`)}</h3>
                <SectionList
                  sections={slots[slot][side]}
                  max={8}
                  locales={locales}
                  choices={choices}
                  imageOptions={imageOptions}
                  linkOptions={linkOptions}
                  typeLabel={t('admin.theme.slotSectionType', {
                    place: t(`admin.theme.place.${slot}.${side}`),
                  })}
                  addLabel={t('admin.theme.addSlotSection', {
                    place: t(`admin.theme.place.${slot}.${side}`),
                  })}
                  onChange={(sections) => {
                    setSlotSide(slot, side, sections);
                  }}
                />
              </div>
            ))}
          </fieldset>
        ))}

        {canManage ? (
          <div className="admin-actions">
            <Button
              type="submit"
              busy={busy}
              disabled={
                handles !== null || textContrast < MIN_CONTRAST || buttonContrast < MIN_CONTRAST
              }
            >
              {t('admin.theme.save')}
            </Button>
            <Button type="button" variant="secondary" busy={busy} onClick={() => void preview()}>
              {t('admin.theme.preview')}
            </Button>
            <Button
              type="button"
              variant="danger"
              busy={busy}
              disabled={
                handles !== null || textContrast < MIN_CONTRAST || buttonContrast < MIN_CONTRAST
              }
              onClick={() => void publish()}
            >
              {t('admin.theme.publish')}
            </Button>
          </div>
        ) : (
          <Button type="button" variant="secondary" busy={busy} onClick={() => void preview()}>
            {t('admin.theme.preview')}
          </Button>
        )}
      </form>
      {previewUrl === null ? null : (
        <p>
          <a href={previewUrl} target="_blank" rel="noreferrer">
            {t('admin.theme.openPreview')}
          </a>{' '}
          <span className="mrigl-muted">{t('admin.theme.previewHint')}</span>
        </p>
      )}

      <section className="mrigl-card mrigl-stack" aria-labelledby="theme-history">
        <h2 id="theme-history" className="mrigl-subheading">
          {t('admin.theme.history')}
        </h2>
        {history.length === 0 ? (
          <p className="mrigl-muted">{t('admin.theme.noHistory')}</p>
        ) : (
          <ul className="mrigl-list">
            {history.map((item) => (
              <li key={item.number} className="admin-row">
                <span>
                  {t('admin.theme.version', { number: item.number })}
                  {item.current ? ` · ${t('admin.theme.current')}` : ''}
                </span>
                {canManage && !item.current ? (
                  <Button
                    variant="secondary"
                    busy={busy}
                    onClick={() =>
                      void run(
                        () =>
                          unwrap(
                            api.POST(
                              '/admin-api/v1/stores/{storeId}/theme/publications/{number}/restore',
                              {
                                params: { path: { storeId: store.storeId, number: item.number } },
                                body: { version: state.version },
                              },
                            ),
                          ),
                        t('admin.theme.restored'),
                      )
                    }
                  >
                    {t('admin.theme.restore', { number: item.number })}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function SectionList({
  sections,
  max,
  locales,
  choices,
  imageOptions,
  linkOptions,
  typeLabel,
  addLabel,
  onChange,
}: {
  readonly sections: readonly Section[];
  readonly max: number;
  readonly locales: readonly Locale[];
  readonly choices: Choices;
  readonly imageOptions: readonly { value: string; label: string }[];
  readonly linkOptions: readonly { value: string; label: string }[];
  readonly typeLabel: string;
  readonly addLabel: string;
  readonly onChange: (sections: Section[]) => void;
}) {
  const { t } = useI18n();
  const [newType, setNewType] = useState<SectionType>('hero');
  return (
    <>
      {sections.map((section, index) => (
        <SectionEditor
          key={section.id}
          section={section}
          number={index + 1}
          count={sections.length}
          locales={locales}
          choices={choices}
          imageOptions={imageOptions}
          linkOptions={linkOptions}
          onChange={(next) => {
            onChange(sections.map((other) => (other.id === section.id ? next : other)));
          }}
          onMove={(delta) => {
            onChange(moveItem(sections, index, delta));
          }}
          onRemove={() => {
            onChange(sections.filter((other) => other.id !== section.id));
          }}
        />
      ))}
      {sections.length < max ? (
        <div className="admin-actions">
          <SelectField
            label={typeLabel}
            value={newType}
            onChange={(event) => {
              setNewType(event.target.value as SectionType);
            }}
            options={SECTION_TYPES.filter(
              (type) => type !== 'featured-collection' || choices.collections.length > 0,
            ).map((type) => ({
              value: type,
              label: t(`admin.theme.section.${type}`),
            }))}
          />
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              const section = newSection(newType, choices.collections[0]?.id);
              if (section !== null) onChange([...sections, section]);
            }}
          >
            {addLabel}
          </Button>
        </div>
      ) : null}
    </>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}) {
  const id = useId();
  return (
    <div className="mrigl-field">
      <label className="mrigl-field__label" htmlFor={id}>
        {label}
      </label>
      <div className="admin-color">
        <input
          id={id}
          type="color"
          value={value}
          onChange={(event) => {
            onChange(event.target.value.toLowerCase());
          }}
        />
        <span className="mrigl-ltr">{value}</span>
      </div>
    </div>
  );
}

function LocalizedFields({
  label,
  value,
  locales,
  max,
  multiline,
  onChange,
}: {
  readonly label: (language: string) => string;
  readonly value: Localized;
  readonly locales: readonly Locale[];
  readonly max: number;
  readonly multiline?: boolean;
  readonly onChange: (value: Localized) => void;
}) {
  const { t } = useI18n();
  return (
    <>
      {locales.map((code) => {
        const props = {
          lang: code,
          dir: 'auto' as const,
          maxLength: max,
          label: label(t(`admin.language.${code}`)),
          value: value[code] ?? '',
        };
        return multiline === true ? (
          <TextAreaField
            key={code}
            {...props}
            onChange={(event) => {
              onChange({ ...value, [code]: event.target.value });
            }}
          />
        ) : (
          <TextField
            key={code}
            {...props}
            onChange={(event) => {
              onChange({ ...value, [code]: event.target.value });
            }}
          />
        );
      })}
    </>
  );
}

function SectionEditor({
  section,
  number,
  count,
  locales,
  choices,
  imageOptions,
  linkOptions,
  onChange,
  onMove,
  onRemove,
}: {
  readonly section: Section;
  readonly number: number;
  readonly count: number;
  readonly locales: readonly Locale[];
  readonly choices: Choices;
  readonly imageOptions: readonly { value: string; label: string }[];
  readonly linkOptions: readonly { value: string; label: string }[];
  readonly onChange: (section: Section) => void;
  readonly onMove: (delta: -1 | 1) => void;
  readonly onRemove: () => void;
}) {
  const { t } = useI18n();
  const heading = (
    <LocalizedFields
      label={(language) => t('admin.theme.headingIn', { language })}
      value={section.heading}
      locales={locales}
      max={120}
      onChange={(value) => {
        onChange({ ...section, heading: value });
      }}
    />
  );
  return (
    <fieldset className="admin-option mrigl-stack">
      <legend>
        {t('admin.theme.sectionN', { number, type: t(`admin.theme.section.${section.type}`) })}
      </legend>
      {heading}
      {section.type === 'hero' ? (
        <>
          <LocalizedFields
            label={(language) => t('admin.theme.textIn', { language })}
            value={section.text}
            locales={locales}
            max={300}
            onChange={(value) => {
              onChange({ ...section, text: value });
            }}
          />
          <SelectField
            label={t('admin.theme.image')}
            value={section.imageMediaId ?? ''}
            onChange={(event) => {
              onChange({
                ...section,
                imageMediaId: event.target.value === '' ? null : event.target.value,
              });
            }}
            options={imageOptions}
          />
          <LocalizedFields
            label={(language) => t('admin.theme.buttonLabelIn', { language })}
            value={section.buttonLabel}
            locales={locales}
            max={40}
            onChange={(value) => {
              onChange({ ...section, buttonLabel: value });
            }}
          />
          <SelectField
            label={t('admin.theme.buttonLink')}
            value={linkValue(section.buttonLink)}
            onChange={(event) => {
              onChange({ ...section, buttonLink: parseLink(event.target.value) });
            }}
            options={linkOptions}
          />
        </>
      ) : null}
      {section.type === 'featured-collection' ? (
        <>
          <SelectField
            label={t('admin.theme.collection')}
            value={section.collectionId}
            onChange={(event) => {
              onChange({ ...section, collectionId: event.target.value });
            }}
            options={choices.collections.map((item) => ({ value: item.id, label: item.name }))}
          />
          <TextField
            type="number"
            min={1}
            max={24}
            label={t('admin.theme.limit')}
            value={String(section.limit)}
            onChange={(event) => {
              onChange({
                ...section,
                limit: Math.min(24, Math.max(1, Number(event.target.value) || 1)),
              });
            }}
          />
        </>
      ) : null}
      {section.type === 'product-grid' ? (
        <TextField
          type="number"
          min={1}
          max={48}
          label={t('admin.theme.limit')}
          value={String(section.limit)}
          onChange={(event) => {
            onChange({
              ...section,
              limit: Math.min(48, Math.max(1, Number(event.target.value) || 1)),
            });
          }}
        />
      ) : null}
      {section.type === 'rich-text' || section.type === 'image-with-text' ? (
        <LocalizedFields
          label={(language) => t('admin.theme.textIn', { language })}
          value={section.body}
          locales={locales}
          max={section.type === 'rich-text' ? 2000 : 1000}
          multiline
          onChange={(value) => {
            onChange({ ...section, body: value });
          }}
        />
      ) : null}
      {section.type === 'image-with-text' ? (
        <>
          <SelectField
            label={t('admin.theme.image')}
            value={section.imageMediaId ?? ''}
            onChange={(event) => {
              onChange({
                ...section,
                imageMediaId: event.target.value === '' ? null : event.target.value,
              });
            }}
            options={imageOptions}
          />
          <SelectField
            label={t('admin.theme.imagePosition')}
            value={section.imagePosition}
            onChange={(event) => {
              onChange({ ...section, imagePosition: event.target.value as 'start' | 'end' });
            }}
            options={[
              { value: 'start', label: t('admin.theme.imagePosition.start') },
              { value: 'end', label: t('admin.theme.imagePosition.end') },
            ]}
          />
        </>
      ) : null}
      <div className="admin-actions">
        <Button
          type="button"
          variant="secondary"
          disabled={number === 1}
          onClick={() => {
            onMove(-1);
          }}
        >
          {t('admin.theme.moveUp', { number })}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={number === count}
          onClick={() => {
            onMove(1);
          }}
        >
          {t('admin.theme.moveDown', { number })}
        </Button>
        <Button type="button" variant="secondary" onClick={onRemove}>
          {t('admin.theme.remove', { number })}
        </Button>
      </div>
    </fieldset>
  );
}
